'use client'

import { ChevronDownIcon, ClearIcon } from '../../../icons'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../../constants/iconButtonAriaLabelMessageKeys'
import { DictionaryContext } from '../../../../providers'
import { SelectOption } from '../../../../types'
import { forwardRef, useContext } from 'react'
import React from 'react'

export interface SelectTriggerButtonProps {
    baseClasses: string
    disabled?: boolean
    isOpen: boolean
    onToggle: () => void
    onKeyDown: (keyboardEvent: React.KeyboardEvent) => void
    id?: string
    dataTestId?: string
    selected: (string | number)[]
    options: SelectOption[]
    listboxId: string
    ariaActivedescendant: string | undefined
    required?: boolean
    clearable?: boolean
    onClear?: () => void
}

/**
 * Trigger button for the HTMLSelect combobox. Shows selected label(s), optional clear button when clearable,
 * and chevron. Used by both SingleSelectCombobox and MultiSelectCombobox.
 * Renders a combobox (role="combobox") with listbox expand/collapse and optional clear action.
 */
export const SelectTriggerButton = forwardRef<HTMLButtonElement, SelectTriggerButtonProps>(
    (
        {
            baseClasses,
            disabled = false,
            isOpen,
            onToggle,
            onKeyDown,
            id,
            dataTestId,
            selected,
            options,
            listboxId,
            ariaActivedescendant,
            required = false,
            clearable = false,
            onClear,
        },
        ref,
    ) => {
        const dictionary = useContext(DictionaryContext)
        const selectedLabels =
            selected.length > 0
                ? selected.map((value) => options.find((option) => option.value == value)?.label ?? String(value))
                : []
        const hasSelection = selected.length > 0
        const showClear = clearable && hasSelection && !disabled && onClear

        return (
            <div className={`${baseClasses} relative flex items-center gap-1 ${disabled ? 'cursor-auto' : ''}`}>
                <button
                    ref={ref}
                    type="button"
                    role="combobox"
                    onClick={onToggle}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={listboxId}
                    aria-activedescendant={ariaActivedescendant}
                    aria-required={required}
                    className={`min-w-0 flex-1 flex items-center justify-between gap-2 text-left py-0 border-0 bg-transparent focus:outline-hidden focus:ring-1 focus:ring-accent-600 focus:ring-inset ${
                        disabled ? 'cursor-auto' : 'cursor-pointer'
                    } ${showClear ? 'pr-0' : ''}`}
                    data-testid={dataTestId}
                >
                    <span className="block break-words min-w-0 flex-1">
                        {selectedLabels.length > 0 && selectedLabels.join(', ')}
                    </span>
                    {!showClear && (
                        <ChevronDownIcon
                            className={`shrink-0 transition-transform text-text-interactive ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    )}
                </button>
                {showClear && (
                    <>
                        <button
                            type="button"
                            onClick={(mouseEvent) => {
                                mouseEvent.stopPropagation()
                                onClear?.()
                            }}
                            onKeyDown={(keyboardEvent) => keyboardEvent.stopPropagation()}
                            disabled={disabled}
                            aria-label={resolveIconButtonAriaLabel(
                                dictionary,
                                ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.SELECT_CLEAR,
                            )}
                            className="shrink-0 rounded text-text-interactive hover:bg-shade-100 focus:outline-hidden focus:ring-1 focus:ring-accent-600 focus:ring-inset"
                            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
                        >
                            <ClearIcon />
                        </button>
                        <ChevronDownIcon
                            className={`shrink-0 transition-transform text-text-interactive ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </>
                )}
            </div>
        )
    },
)
